import type { PlainMessage } from '@bufbuild/protobuf';
import * as RadixPopover from '@radix-ui/react-popover';
import cn from 'classnames';
import { noop } from 'lodash-es';
import {
  cloneElement,
  createContext,
  FC,
  Fragment,
  isValidElement,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NoteAddedBlockOp } from '@/shared/api/protocol-ts/api/scribe/svc_encounter_pb';
import {
  NoteBlockWidget,
  NoteNumberQualifier,
  NoteTeethGroup,
  type NoteDateBlock,
  type NoteList,
  type NoteNumberBlock,
  type NoteNumberValue,
  type NoteTeethBlock,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { NoteToothNotation } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';
import { Icon } from '@/shared/ui';

import {
  listNoteChoiceSpellings,
  noteChoiceImage,
  noteChoiceLabel,
  noteChoicePrint,
  noteTeethHaveMarks,
  splitInlineGuidance,
  type SectionOption,
} from '@/entities/templates';

import {
  NOTE_FREE_TEXT_MAX_LENGTH,
  NOTE_NUMBER_MIN_CHARS,
  NOTE_OPTION_IMAGES_SHOWN,
  NOTE_OPTIONS_VISIBLE,
  NOTE_OVERRIDE_MAX_LENGTH,
  NOTE_OWN_VALUES_MAX_COUNT,
} from '../config';
import { useNoteTeethGroupName } from '../hooks';
import {
  addNoteOwnOption,
  answerKey,
  applyNoteCorrection,
  changeNoteAddedBlock,
  clearNoteBlock,
  confirmNoteBlock,
  describeAiBasis,
  describeField,
  dismissNoteCorrection,
  endNoteValueBeforePeriod,
  fillBlanks,
  findChosenValue,
  findNamedNoteValue,
  formatAnswerValue,
  formatSlotName,
  getBlankUnits,
  getNoteBlockLabel,
  getNoteBlockValues,
  getNoteContextKeyMessage,
  getOptionSlots,
  getSlotValues,
  hasBlanks,
  isNoteBlockMultiple,
  isNoteFieldNamedByLine,
  isNotePeriodAfter,
  isNoteOtherAllowed,
  lastChosenValueNode,
  listHeldNoteValues,
  listNoteBlockMarks,
  listTextBlockAside,
  matchBlankFills,
  printNoteChoice,
  printNoteDate,
  printNoteNone,
  printNoteNumbers,
  printNoteQualifier,
  printNoteTeeth,
  printNoteTeethMarks,
  printNoteUnit,
  readChosenValueOpen,
  releaseNoteBlock,
  removeNoteOwnOption,
  replaceAnswerValue,
  resolveAnswer,
  revertNoteBlock,
  setNoteBlock,
  splitAnswerValues,
  splitBlanks,
  splitPhrase,
  startsWithPunctuation,
  stripInlineGuidance,
  toggleAnswerValue,
  type DraftAnswers,
  type NoteBlockDef,
  formatNoteDecimal,
  isNoteNumberInRange,
  parseNoteDecimal,
  type NoteBlockSelection,
  type NoteMark,
  type NoteOperation,
  type ResolvedAnswer,
  type SurfaceAiBasis,
  type SurfaceBlock,
  type SurfaceLine,
} from '../lib';

import { AiBasisMark } from './AiBasisMark';
import { AiBasisQuote } from './AiBasisQuote';
import { AiMark } from './AiMark';
import { AssumptionActions } from './AssumptionActions';
import { InlineText } from './InlineText';
import { InstructionLine } from './InstructionLine';
import { NoteDatePicker } from './NoteDatePicker';
import { NoteOptionImage } from './NoteOptionImage';
import { NoteTableField } from './NoteTableField';
import { NoteTeethMarks } from './NoteTeethMarks';
import { NoteTeethPicker } from './NoteTeethPicker';
import { PartnerDefaultHint } from './PartnerDefaultHint';
import { PartnerTeethMissing } from './PartnerTeethMissing';
import styles from './TemplateFieldView.module.scss';

// Where a template is read without a note (the Templates page), an option
// names the lines it opens: how many, by its block and value. A note keeps
// its links quiet and sets none.
export const NoteOptionLinksContext = createContext<
  (blockId: string, value: string) => number
>(() => 0);

type ChoiceProps = {
  selected: boolean;
  ai: boolean;
  // A block-note AI value: what it rests on, and the words said to show in
  // this chip.
  basis?: SurfaceAiBasis | null;
  said?: string;
  rationale?: string;
  aiHint?: string;
  // The value the chip offers, where a link to it leads.
  optionValue?: string;
  // The lines the option opens, marked with the chain after its label.
  opens?: number;
  // An option of a block note reads as text of its line; the templates
  // with options keep their chips.
  asText?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
};

const Choice: FC<ChoiceProps> = ({
  selected,
  ai,
  basis = null,
  said,
  rationale,
  aiHint,
  optionValue,
  opens = 0,
  asText = false,
  children,
  className,
  disabled,
  onClick,
}) => {
  const { formatMessage } = useIntl();
  const opensLabel = formatMessage(
    {
      id: 'workspace.note.linkOpens',
      defaultMessage: 'opens {count, plural, one {# line} other {# lines}}',
    },
    { count: opens },
  );

  return (
    <button
      type="button"
      data-option-value={optionValue}
      className={cn(
        'p2',
        asText ? styles.textOption : styles.choice,
        ai && styles.aiChoice,
        ai && basis === 'assumed' && styles.assumedChoice,
        className,
      )}
      aria-pressed={selected}
      disabled={disabled}
      title={
        ai
          ? aiHint ??
            formatMessage({
              id: 'workspace.template.aiChoiceHint',
              defaultMessage: 'Filled by AI. Click to confirm it manually.',
            })
          : undefined
      }
      onClick={onClick}
    >
      {children}
      {opens > 0 && (
        <span
          className={styles.optionLinks}
          role="img"
          aria-label={opensLabel}
          title={opensLabel}
          data-component="workspace-note-option-opens"
        >
          <Icon name="link" size={14} />
        </span>
      )}
      {ai && (
        <>
          {' '}
          <AiBasisMark basis={basis} said={said} rationale={rationale} />
        </>
      )}
    </button>
  );
};

// Hands a field the clinician answered back to AI: the next live fill may
// set it again.
export const ReleaseButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const { formatMessage } = useIntl();
  const label = formatMessage({
    id: 'workspace.live.release',
    defaultMessage: 'Hand back to AI',
  });

  return (
    <button
      type="button"
      className={cn('p4', styles.release)}
      title={label}
      onClick={onClick}
    >
      {label}
    </button>
  );
};

// A manual choice toggles off; an AI choice is confirmed as manual first,
// keeping the text AI wrote into its blanks.
const nextChoice = (
  answer: ResolvedAnswer,
  allowed: string[],
  item: string,
) => {
  const chosen = findChosenValue(answer.value, allowed, item);
  if (chosen === undefined) {
    return item;
  }
  return answer.source === 'manual' ? '' : chosen;
};

type BlankChoiceProps = {
  item: string;
  value: string;
  ai: boolean;
  autoFocus: boolean;
  onToggle: () => void;
  onFill: (value: string) => void;
};

// A chosen value with blanks: each blank is typed in place together with its
// unit, which the empty input shows as a hint, and the value is sent once
// every blank is filled or cleared, so a half-filled value that the server
// refuses is never sent.
const BlankChoice: FC<BlankChoiceProps> = ({
  item,
  value,
  ai,
  autoFocus,
  onToggle,
  onFill,
}) => {
  const { formatMessage } = useIntl();
  const parts = splitBlanks(item);
  const units = getBlankUnits(item);
  const stored = matchBlankFills(item, value) ?? units.map(() => '');
  const [typed, setTyped] = useState<string[] | null>(null);
  const cancelledRef = useRef(false);
  const fills = typed ?? stored;

  const commit = () => {
    const cancelled = cancelledRef.current;
    cancelledRef.current = false;
    if (cancelled) {
      setTyped(null);
      return;
    }
    const complete =
      fills.every((fill) => fill.trim()) || fills.every((fill) => !fill.trim());
    if (!typed || !complete) {
      return;
    }
    setTyped(null);
    const next = fillBlanks(item, fills);
    if (next !== value) {
      onFill(next);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
    if (event.key === 'Escape') {
      cancelledRef.current = true;
      setTyped(null);
      event.currentTarget.blur();
    }
  };

  return (
    <span
      className={cn(
        'p2',
        styles.choice,
        styles.blankChoice,
        ai && styles.aiChoice,
      )}
    >
      <button
        type="button"
        className={styles.blankToggle}
        aria-pressed
        aria-label={item}
        title={
          ai
            ? formatMessage({
                id: 'workspace.template.aiChoiceHint',
                defaultMessage: 'Filled by AI. Click to confirm it manually.',
              })
            : undefined
        }
        onClick={onToggle}
      >
        {parts[0].trim() ? parts[0] : <Icon name="check" size={16} />}
      </button>
      {parts.slice(1).map((part, index) => (
        <span key={index} className={styles.blankPart}>
          <input
            className={styles.blankInput}
            aria-label={formatMessage(
              {
                id: 'workspace.template.blank',
                defaultMessage:
                  '{count, plural, one {Blank in “{value}”} other {Blank {number} in “{value}”}}',
              },
              { count: parts.length - 1, number: index + 1, value: item },
            )}
            autoFocus={autoFocus && index === 0}
            placeholder={units[index]}
            size={Math.max(fills[index].length, units[index].length, 3)}
            value={fills[index]}
            onChange={(event) => {
              const text = event.target.value;
              setTyped(
                fills.map((fill, fillIndex) =>
                  fillIndex === index ? text : fill,
                ),
              );
            }}
            onBlur={commit}
            onKeyDown={onKeyDown}
          />
          {part}
        </span>
      ))}
      {ai && <AiMark />}
    </span>
  );
};

type ValueChipProps = {
  item: string;
  chosen: string | undefined;
  ai: boolean;
  onToggle: () => void;
  onFill: (value: string) => void;
};

const ValueChip: FC<ValueChipProps> = ({
  item,
  chosen,
  ai,
  onToggle,
  onFill,
}) => {
  const [choosing, setChoosing] = useState(false);

  if (chosen === undefined || !hasBlanks(item)) {
    return (
      <Choice
        selected={chosen !== undefined}
        ai={ai}
        onClick={() => {
          setChoosing(chosen === undefined);
          onToggle();
        }}
      >
        {item}
      </Choice>
    );
  }
  return (
    <BlankChoice
      item={item}
      value={chosen}
      ai={ai}
      autoFocus={choosing}
      onToggle={onToggle}
      onFill={onFill}
    />
  );
};

type SlotValueProps = {
  label: string;
  values: string[];
  answer: ResolvedAnswer;
  multiple: boolean;
  onChange: (value: string) => void;
  // Lets a single-choice slot take one more value; a click still replaces.
  onAllowMultiple?: () => void;
};

const SlotValue: FC<SlotValueProps> = ({
  label,
  values,
  answer,
  multiple,
  onChange,
  onAllowMultiple,
}) => {
  const { formatMessage } = useIntl();
  const ai = answer.source === 'ai' && !!answer.value;
  const chosenCount = splitAnswerValues(answer.value).length;

  const chips = values.map((item) => {
    const chosen = findChosenValue(answer.value, values, item);
    return (
      <ValueChip
        key={item}
        item={item}
        chosen={chosen}
        ai={ai && chosen !== undefined}
        onToggle={() =>
          onChange(
            multiple
              ? toggleAnswerValue(answer.value, item, values)
              : nextChoice(answer, values, item),
          )
        }
        onFill={(filled) =>
          onChange(
            multiple
              ? replaceAnswerValue({
                  value: answer.value,
                  allowed: values,
                  item,
                  next: filled,
                })
              : filled,
          )
        }
      />
    );
  });

  return (
    <span className={values.length ? styles.choices : styles.freeValue}>
      {values.length ? (
        chips
      ) : (
        <>
          <input
            className={cn('p2', styles.freeInput, ai && styles.aiText)}
            aria-label={label}
            placeholder={formatMessage({
              id: 'workspace.template.fillLater',
              defaultMessage: 'Filled from the recording',
            })}
            value={answer.value}
            onChange={(event) => onChange(event.target.value)}
          />
          {ai && <AiMark />}
        </>
      )}
      {onAllowMultiple &&
        !multiple &&
        chosenCount === 1 &&
        values.length > 1 && (
          <button
            type="button"
            className={cn(
              'p2',
              styles.choice,
              styles.dashedChoice,
              styles.addValue,
            )}
            title={formatMessage({
              id: 'workspace.template.addAnotherValueHint',
              defaultMessage: 'Choose one more value',
            })}
            onClick={onAllowMultiple}
          >
            <FormattedMessage
              id="workspace.template.addAnotherValue"
              defaultMessage="+ another"
            />
          </button>
        )}
      {multiple && values.length > 0 && (
        <span className={cn('p4', styles.multipleHint)}>
          <FormattedMessage
            id="workspace.template.multipleHint"
            defaultMessage="several allowed"
          />
        </span>
      )}
    </span>
  );
};

type InlineSlotProps = {
  label: string;
  placeholder: string;
  answer: ResolvedAnswer;
  onChange: (value: string) => void;
};

// Free values are typed inside the sentence, so a phrase with many of them
// stays readable instead of turning into a list of empty rows.
const InlineSlot: FC<InlineSlotProps> = ({
  label,
  placeholder,
  answer,
  onChange,
}) => (
  <input
    className={cn(
      styles.inlineSlot,
      answer.value && styles.inlineFilled,
      answer.source === 'ai' && answer.value && styles.inlineAi,
    )}
    aria-label={label}
    placeholder={placeholder}
    size={Math.max(answer.value.length, placeholder.length, 4)}
    value={answer.value}
    onChange={(event) => onChange(event.target.value)}
  />
);

type FieldInstructionsProps = {
  option: SectionOption;
};

// The field instruction and one instruction per value of a phrase. A labeled
// field has one value, so its field instruction covers it; an instruction of
// that value is only shown when a template already has one.
const FieldInstructions: FC<FieldInstructionsProps> = ({ option }) => {
  const { formatMessage } = useIntl();
  const field = describeField(option);
  const slots =
    field.kind === 'phrase' ||
    (field.kind === 'labeled' && option.SlotInstructions[field.slot]?.trim())
      ? getOptionSlots(option)
      : [];
  const instructions = [
    option.Instructions,
    ...slots.map((slot) => option.SlotInstructions[slot]),
  ];
  if (instructions.every((instruction) => !instruction?.trim())) {
    return null;
  }

  return (
    <div className={styles.instructions}>
      <InstructionLine
        value={option.Instructions ?? ''}
        label={formatMessage({
          id: 'workspace.instructions',
          defaultMessage: 'AI instruction',
        })}
        placeholder={formatMessage({
          id: 'workspace.template.addInstruction',
          defaultMessage: '+ AI instruction',
        })}
        editable={false}
        onCommit={noop}
      />
      {slots.map((slot) => {
        const name = formatSlotName(slot);
        return (
          <InstructionLine
            key={slot}
            className={styles.slotInstruction}
            value={option.SlotInstructions[slot] ?? ''}
            label={formatMessage(
              {
                id: 'workspace.template.slotInstructionFor',
                defaultMessage: 'AI instruction for “{name}”',
              },
              { name },
            )}
            placeholder={formatMessage(
              {
                id: 'workspace.template.addSlotInstruction',
                defaultMessage: '+ AI instruction for “{name}”',
              },
              { name },
            )}
            editable={false}
            onCommit={noop}
          />
        );
      })}
    </div>
  );
};

type BodyProps = {
  sectionCode: string;
  sectionTitle: string;
  optionIndex: number;
  option: SectionOption;
  answers: DraftAnswers;
  multiSelect: Record<string, true>;
  className?: string;
  onAnswer: (key: string, value: string) => void;
  onRelease?: (key: string) => void;
  onAllowMultiple?: (key: string) => void;
};

const FieldBody: FC<BodyProps> = ({
  sectionCode,
  sectionTitle,
  optionIndex,
  option,
  answers,
  multiSelect,
  className,
  onAnswer,
  onRelease,
  onAllowMultiple,
}) => {
  const { formatMessage } = useIntl();
  const field = describeField(option);
  const slotKey = (slot: string) => answerKey(sectionCode, optionIndex, slot);
  const allowMultiple = (slot: string) =>
    onAllowMultiple && (() => onAllowMultiple(slotKey(slot)));
  const slotAnswer = (slot: string) => resolveAnswer(answers, slotKey(slot));
  const fieldKeys =
    field.kind === 'statement'
      ? [answerKey(sectionCode, optionIndex)]
      : getOptionSlots(option).map(slotKey);
  const manualKeys = fieldKeys.filter(
    (key) => resolveAnswer(answers, key).source === 'manual',
  );
  const release = onRelease && manualKeys.length > 0 && (
    <ReleaseButton
      onClick={() => manualKeys.forEach((key) => onRelease(key))}
    />
  );

  if (field.kind === 'statement') {
    const key = answerKey(sectionCode, optionIndex);
    const answer = resolveAnswer(answers, key);
    return (
      <div className={cn(styles.row, className)}>
        <Choice
          selected={!!answer.value}
          ai={answer.source === 'ai' && !!answer.value}
          onClick={() =>
            onAnswer(key, nextChoice(answer, [option.Text], option.Text))
          }
        >
          {option.Text}
        </Choice>
        {release}
      </div>
    );
  }

  if (field.kind === 'labeled') {
    return (
      <div className={cn(styles.row, className)}>
        <span className={cn('p2', styles.label)}>
          <InlineText
            value={field.label}
            label={formatMessage({
              id: 'workspace.template.fieldName',
              defaultMessage: 'Field name',
            })}
            editable={false}
            onCommit={noop}
          />
        </span>
        <SlotValue
          label={`${sectionTitle}: ${field.label || formatSlotName(field.slot)}`}
          values={getSlotValues(option, field.slot)}
          answer={slotAnswer(field.slot)}
          multiple={!!multiSelect[slotKey(field.slot)]}
          onChange={(value) => onAnswer(slotKey(field.slot), value)}
          onAllowMultiple={allowMultiple(field.slot)}
        />
        {release}
      </div>
    );
  }

  const strictSlots = field.slots.filter(
    (slot) => getSlotValues(option, slot).length > 0,
  );

  return (
    <div className={cn(styles.phrase, className)}>
      <p className={cn('p2', styles.phraseText)}>
        {splitPhrase(option.Text).map((part, index) => {
          if (!('slot' in part)) {
            return part.text;
          }
          const answer = slotAnswer(part.slot);
          if (!strictSlots.includes(part.slot)) {
            return (
              <InlineSlot
                key={index}
                label={`${sectionTitle}: ${formatSlotName(part.slot)}`}
                placeholder={formatSlotName(part.slot)}
                answer={answer}
                onChange={(value) => onAnswer(slotKey(part.slot), value)}
              />
            );
          }
          return (
            <mark
              key={index}
              className={cn(
                styles.slotMark,
                answer.value && styles.slotFilled,
                answer.value && answer.source === 'ai' && styles.slotAi,
              )}
            >
              {formatAnswerValue(answer.value) || formatSlotName(part.slot)}
            </mark>
          );
        })}
      </p>
      {strictSlots.map((slot) => (
        <div key={slot} className={styles.row}>
          <span className={cn('p2', styles.slotLabel)}>
            {formatSlotName(slot)}
          </span>
          <SlotValue
            label={`${sectionTitle}: ${formatSlotName(slot)}`}
            values={getSlotValues(option, slot)}
            answer={slotAnswer(slot)}
            multiple={!!multiSelect[slotKey(slot)]}
            onChange={(value) => onAnswer(slotKey(slot), value)}
            onAllowMultiple={allowMultiple(slot)}
          />
        </div>
      ))}
      {release}
    </div>
  );
};

const OrphanSlots: FC<{ slots: string[] }> = ({ slots }) => (
  <>
    {slots.map((slot) => (
      <p key={slot} className={cn('p4', styles.warning)}>
        <FormattedMessage
          id="workspace.template.orphanSlotNotPrinted"
          defaultMessage="“{name}” is not in the field text, so this field is left out of the document."
          values={{ name: formatSlotName(slot) }}
        />
      </p>
    ))}
  </>
);

type Props = BodyProps & {
  showInstructions: boolean;
  // Slots of the field that its text does not name.
  orphanSlots?: string[];
};

export const TemplateFieldView: FC<Props> = ({
  showInstructions,
  orphanSlots = [],
  className,
  ...body
}) =>
  showInstructions || orphanSlots.length > 0 ? (
    <div className={cn(styles.field, className)}>
      <FieldBody {...body} />
      <OrphanSlots slots={orphanSlots} />
      {showInstructions && <FieldInstructions option={body.option} />}
    </div>
  ) : (
    <FieldBody className={className} {...body} />
  );

// Block templates. A line reads as one sentence of its blocks, and every
// option is a chip. A template read before any note shows its values only;
// the fill surface prints a chosen value as text with the other options
// beside it.

// template: a template read before any note, its values only shown;
// prefill: the fill surface before the recording, with no encounter yet;
// note: the fill surface from scribe's view of the encounter's note.
export type LineMode = 'template' | 'prefill' | 'note';

type InlineValueProps = {
  value: string;
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
  maxLength?: number;
  className?: string;
  onCommit: (value: string) => void;
  onCancel?: () => void;
};

// A value typed into the sentence: Enter or leaving it keeps the text,
// Escape restores it. Until it is focused it shows the stored value, so a
// server update is never hidden behind an old draft.
export const InlineValueInput: FC<InlineValueProps> = ({
  value,
  label,
  placeholder,
  autoFocus = false,
  maxLength,
  className,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const text = draft ?? value;
  const end = () => {
    const cancelled = cancelledRef.current;
    cancelledRef.current = false;
    setDraft(null);
    const next = text.trim();
    if (cancelled || next === value.trim() || !next) {
      onCancel?.();
      return;
    }
    onCommit(next);
  };

  return (
    <input
      className={cn(
        'p2',
        styles.valueInput,
        value && styles.valueFilled,
        className,
      )}
      aria-label={label}
      placeholder={placeholder}
      autoFocus={autoFocus}
      maxLength={maxLength}
      size={Math.max(text.length, placeholder?.length ?? 0, 6)}
      value={text}
      onFocus={() => setDraft(value)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={end}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          cancelledRef.current = true;
          event.currentTarget.blur();
        }
      }}
    />
  );
};

// Fixed wording with its inline guidance set apart: the bracketed spans
// reach the AI, never the note, so they are set apart on the template only.
const Wording: FC<{ value: string }> = ({ value }) => (
  <>
    {splitInlineGuidance(value).map((segment, index) =>
      segment.guidance ? (
        <span
          key={index}
          className={styles.inlineGuidance}
          data-component="workspace-template-inline-guidance"
        >
          {segment.text}
        </span>
      ) : (
        <Fragment key={index}>{segment.text}</Fragment>
      ),
    )}
  </>
);

type ToolsProps = {
  block: SurfaceBlock;
  listening: boolean;
  onRevert: () => void;
  onRelease: () => void;
};

// What a manual value may give back: the AI value it displaced, or, while
// dictating, the block itself to the live fill.
const ValueTools: FC<ToolsProps> = ({
  block,
  listening,
  onRevert,
  onRelease,
}) => {
  if (block.source !== 'manual') {
    return null;
  }
  if (block.canRevert) {
    return (
      <button
        type="button"
        className={cn('p4', styles.release)}
        onClick={onRevert}
      >
        <FormattedMessage
          id="workspace.note.revertToAi"
          defaultMessage="Back to the AI value"
        />
      </button>
    );
  }
  return listening ? <ReleaseButton onClick={onRelease} /> : null;
};

type CorrectionProps = {
  text: string;
  editable: boolean;
  onApply: () => void;
  onDismiss: () => void;
};

// A later AI reading of a value AI already set: never applied by itself.
const CorrectionHint: FC<CorrectionProps> = ({
  text,
  editable,
  onApply,
  onDismiss,
}) => {
  const { formatMessage } = useIntl();
  return (
    <span
      className={cn('p2', styles.correction)}
      data-component="workspace-note-correction"
    >
      <FormattedMessage
        id="workspace.note.correction"
        defaultMessage="AI: {value}?"
        values={{ value: text }}
      />
      {editable && (
        <>
          <button
            type="button"
            className={styles.correctionAction}
            aria-label={formatMessage(
              {
                id: 'workspace.note.correctionApply',
                defaultMessage: 'Use “{value}”',
              },
              { value: text },
            )}
            onClick={onApply}
          >
            <Icon name="check" size={14} />
          </button>
          <button
            type="button"
            className={styles.correctionAction}
            aria-label={formatMessage({
              id: 'workspace.note.correctionDismiss',
              defaultMessage: 'Keep the current value',
            })}
            onClick={onDismiss}
          >
            <Icon name="close" size={14} />
          </button>
        </>
      )}
    </span>
  );
};

// The recording modals close on Esc from a window listener. Radix closes
// the popover from its document listener, which runs first, so stopping the
// event there leaves the modal open.
const keepEscapeInPopover = (event: Event) => event.stopPropagation();

type BlockPopoverProps = {
  label: string;
  // A button; it stays the same element whether the pop-up is open or not.
  trigger: ReactElement;
  open: boolean;
  contentClassName: string;
  children: ReactNode;
  disabled?: boolean;
  // The block whose choice asked for the focus on this trigger.
  focusKey?: string;
  // What the pop-up opens under, when not its trigger.
  anchor?: (trigger: HTMLElement) => Element | null;
  onOpenChange: (open: boolean) => void;
};

// A choice from a block's chips replaces them with the printed value, a new
// trigger, in the same block or in the document's own text: the focus is
// handed to that trigger once it mounts, so it never drops to the page.
const focusRequests = new Set<string>();

const requestTriggerFocus = (blockId: string) => focusRequests.add(blockId);

const takeTriggerFocus = (blockId: string) => focusRequests.delete(blockId);

// A block's pop-up of options or teeth. At rest only its trigger renders, as
// a Radix trigger would, and the Radix pop-up mounts when it opens, so a note
// with hundreds of blocks carries no pop-up until one is used. Focus comes
// back to the trigger as Radix brings it: unless the clinician clicked
// outside, or the focus is still where it was, as in the text of the
// document a click on a chosen value left it in.
const BlockPopover: FC<BlockPopoverProps> = ({
  label,
  trigger,
  open,
  contentClassName,
  children,
  disabled = false,
  focusKey,
  anchor,
  onOpenChange,
}) => {
  const triggerRef = useRef<HTMLElement>(null);
  const clickedOutside = useRef(false);
  const openedFrom = useRef<HTMLElement | null>(null);
  const anchorOf = useRef(anchor);
  const contentId = useId();
  // One measured place for the whole life of the pop-up: Radix takes a new
  // anchor object as a new anchor.
  const anchorRef = useRef({
    get contextElement() {
      return triggerRef.current ?? undefined;
    },
    getBoundingClientRect: () => {
      const triggerNode = triggerRef.current;
      const from = openedFrom.current?.isConnected
        ? openedFrom.current
        : triggerNode && anchorOf.current?.(triggerNode);
      return (from ?? triggerNode ?? document.body).getBoundingClientRect();
    },
  });

  useLayoutEffect(() => {
    anchorOf.current = anchor;
  });

  // The document may still move the widget a block renders in, so the focus
  // waits a frame and goes to the trigger that is in the page.
  useEffect(() => {
    if (!focusKey || !focusRequests.has(focusKey)) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      const trigger = triggerRef.current;
      if (trigger?.isConnected && takeTriggerFocus(focusKey)) {
        trigger.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [focusKey]);

  return (
    <>
      {cloneElement(trigger, {
        ref: triggerRef,
        ...(disabled && { disabled }),
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        'aria-controls': contentId,
        'data-state': open ? 'open' : 'closed',
        onClick: (event: MouseEvent<HTMLElement>) => {
          trigger.props.onClick?.(event);
          if (!event.defaultPrevented) {
            openedFrom.current =
              (!open && focusKey && readChosenValueOpen(focusKey)) || null;
            onOpenChange(!open);
          }
        },
      })}
      {open && (
        <RadixPopover.Root open onOpenChange={onOpenChange}>
          <RadixPopover.Anchor virtualRef={anchorRef} />
          <RadixPopover.Content
            id={contentId}
            className={contentClassName}
            side="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={16}
            data-component="workspace-note-options"
            onOpenAutoFocus={(event) => {
              if (openedFrom.current) {
                event.preventDefault();
              }
            }}
            // Opened from the document's text, the caret is still there,
            // and the document keeps the line under it as it is: the caret
            // leaves for the pop-up, so a choice shows in the text at once.
            onPointerDownCapture={(event) => {
              const content = event.currentTarget;
              if (
                openedFrom.current &&
                !content.contains(document.activeElement)
              ) {
                content.focus({ preventScroll: true });
              }
            }}
            onEscapeKeyDown={keepEscapeInPopover}
            onInteractOutside={(event) => {
              // The trigger toggles the pop-up itself.
              if (triggerRef.current?.contains(event.target as Node)) {
                event.preventDefault();
                return;
              }
              clickedOutside.current = true;
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              const focused = document.activeElement;
              const dropped =
                !focused || focused === document.body || !focused.isConnected;
              if (!clickedOutside.current && dropped) {
                triggerRef.current?.focus();
              }
              clickedOutside.current = false;
              openedFrom.current = null;
            }}
          >
            <p className={cn('p4', styles.popoverTitle)}>{label}</p>
            {children}
          </RadixPopover.Content>
        </RadixPopover.Root>
      )}
    </>
  );
};

type MoreOptionsProps = {
  label: string;
  // "More (N)…" after the options in the line, or the printed value of a
  // block that shows its options only on request.
  trigger: ReactElement;
  searchable: boolean;
  listClassName?: string;
  focusKey?: string;
  anchor?: (trigger: HTMLElement) => Element | null;
  children: (options: { query: string; close: () => void }) => ReactNode;
};

// Options in a pop-up, so the line keeps a readable length: the values past
// the first ones of a long list, or every option of a block whose chosen
// value prints as text. A long list is searched. It opens in place, not in a
// portal, so it stays above a modal and keeps the workspace tokens.
const MoreOptions: FC<MoreOptionsProps> = ({
  label,
  trigger,
  searchable,
  listClassName,
  focusKey,
  anchor,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery('');
    }
  };
  // The arrows step from the search to the first row and between the rows.
  const stepRows = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    const list = listRef.current;
    const rows = [
      ...(list?.querySelectorAll<HTMLElement>('[data-option-row]') ?? []),
    ];
    const fromSearch = event.currentTarget !== list;
    const at = fromSearch ? -1 : rows.indexOf(event.target as HTMLElement);
    const next = rows[at + (event.key === 'ArrowDown' ? 1 : -1)];
    if ((!fromSearch && at < 0) || !next) {
      return;
    }
    event.preventDefault();
    next.focus();
  };
  return (
    <BlockPopover
      label={label}
      trigger={trigger}
      open={open}
      contentClassName={styles.popover}
      focusKey={focusKey}
      anchor={anchor}
      onOpenChange={changeOpen}
    >
      {searchable && (
        <input
          className={cn('p2', styles.optionSearch)}
          type="search"
          value={query}
          aria-label={label}
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={stepRows}
        />
      )}
      <div
        ref={listRef}
        className={cn(styles.popoverOptions, listClassName)}
        onKeyDown={stepRows}
      >
        {children({ query, close: () => changeOpen(false) })}
      </div>
    </BlockPopover>
  );
};

const matchesQuery = (value: string, query: string) =>
  value.toLowerCase().includes(query.trim().toLowerCase());

// The options of a block note's line read as one phrase, joined by « · »;
// the dot keeps to the option before it, so a wrapped row never starts with
// it. Pictures stand as tiles, apart.
const joinNoteOptions = (items: ReactNode[], tiles: boolean) => {
  const shown = items.filter((item) => !!item);
  return shown.map((item, index) => {
    const key = isValidElement(item) && item.key ? item.key : index;
    return tiles || index === shown.length - 1 ? (
      <Fragment key={key}>{item}</Fragment>
    ) : (
      <Fragment key={key}>
        <span className={styles.optionItem}>
          {item}
          <span className={cn('p2', styles.optionSeparator)} aria-hidden="true">
            {'\u00a0·'}
          </span>
        </span>{' '}
      </Fragment>
    );
  });
};

type CheckOptionProps = {
  chosen: boolean;
  children: ReactNode;
  after?: ReactNode;
  component?: string;
  toggleComponent?: string;
  onClick: () => void;
};

const CheckOption: FC<CheckOptionProps> = ({
  chosen,
  children,
  after,
  component,
  toggleComponent,
  onClick,
}) => (
  <span className={styles.checkOption} data-component={component}>
    <button
      type="button"
      className={cn('p2', styles.textOption, styles.checkToggle)}
      aria-pressed={chosen}
      data-component={toggleComponent}
      data-option-row
      onClick={onClick}
    >
      <span className={styles.checkMark} aria-hidden="true">
        {chosen && <Icon name="check" size={16} />}
      </span>
      {children}
    </button>
    {after}
  </span>
);

type ChoiceBlockProps = {
  block: SurfaceBlock;
  def: NoteBlockDef;
  lists: NoteList[];
  // The fill surface (prefill, note) prints the chosen values as text with
  // the other options as chips in the line; a template read before any note
  // (template) shows its values as chips only.
  mode: LineMode;
  editable: boolean;
  listening: boolean;
  // Saved to the record: only the text is left.
  approved: boolean;
  // The chosen values are text of the note's document: only the way to the
  // options and the block's marks are left here.
  valueInDocument?: boolean;
  // The template's language, which an explicit none prints in.
  language?: string;
  nameInLine?: boolean;
  onChange: (selection: Pick<NoteBlockSelection, 'values' | 'other'>) => void;
  onNone: (none: boolean) => void;
  onAddOption: (text: string) => void;
  onRemoveOption: (option: string) => void;
  onRevert: () => void;
  onRelease: () => void;
};

// Choice, multi-choice and list blocks, one or several values by the block.
// On the fill surface a long list shows its first values in the line, the
// rest behind "More…", and "Other" always takes typed text, as the block's
// settings bind the AI, not the clinician: every "Other" adds a new option of
// the block in this note, chosen by the block's rule, and the earlier ones
// stay offered. In the note's document the printed values are typed over,
// each on its own, as own values; the template's values keep their wording.
const ChoiceBlock: FC<ChoiceBlockProps> = ({
  block,
  def,
  lists,
  mode,
  editable,
  listening,
  approved,
  valueInDocument = false,
  language,
  nameInLine = false,
  onChange,
  onNone,
  onAddOption,
  onRemoveOption,
  onRevert,
  onRelease,
}) => {
  const optionLinks = useContext(NoteOptionLinksContext);
  const { formatMessage } = useIntl();
  const [typingOther, setTypingOther] = useState(false);
  const offered = getNoteBlockValues(def, lists);
  const choiceKind = def.Kind.value as {
    Widget?: NoteBlockWidget;
    AllowNone?: boolean;
    NoneLabel?: string;
  };
  const images =
    NOTE_OPTION_IMAGES_SHOWN &&
    choiceKind.Widget === NoteBlockWidget.NoteBlockWidgetImages;
  const spellings = (value: string) => listNoteChoiceSpellings(def, value);
  // An option reads as its label, under its picture in the images
  // presentation.
  const optionLabel = (value: string) => (
    <>
      {images && <NoteOptionImage image={noteChoiceImage(def, value)} />}
      {noteChoiceLabel(def, value)}
    </>
  );
  const none = !!block.selection.none;
  // The pictures presentation keeps its options as tiles; every other option
  // reads as text.
  const optionClass = images ? styles.choice : styles.textOption;
  const noneChip = !!choiceKind.AllowNone && (
    <button
      key="none"
      type="button"
      className={cn('p2', optionClass)}
      aria-pressed={none}
      disabled={!editable || approved}
      data-component="workspace-note-none"
      onClick={() => onNone(!none)}
    >
      {printNoteNone(choiceKind, language)}
    </button>
  );
  const multiple = isNoteBlockMultiple(def);
  const asText = mode !== 'template';
  const otherAllowed = asText || isNoteOtherAllowed(def);
  const label =
    getNoteBlockLabel(def, lists) ??
    formatMessage({
      id: 'workspace.note.selectOption',
      defaultMessage: 'Select an option',
    });
  // A name the line says already leaves the empty field its generic one.
  const emptyName = nameInLine
    ? formatMessage({
        id: 'workspace.note.selectOption',
        defaultMessage: 'Select an option',
      })
    : label;
  const { selection } = block;
  const ai = block.source === 'ai';

  if (!offered) {
    return (
      <span
        className={cn(
          'p2',
          styles.valueMark,
          block.resolved && styles.slotFilled,
        )}
      >
        {block.resolved ? (
          block.text
        ) : (
          <FormattedMessage
            id="workspace.note.listLoading"
            defaultMessage="List is loading…"
          />
        )}
      </span>
    );
  }

  const held = multiple ? listHeldNoteValues(selection) : selection.values;
  const other = multiple ? '' : selection.other;
  const ownValues = held.filter((value) => !offered.includes(value));
  const values = [...offered, ...ownValues];
  const isChosen = (value: string) => held.includes(value);
  const lastChosen = values.filter(isChosen).at(-1);
  // An own option the block lists now is that value.
  const ownOptions = block.ownOptions.filter(
    (option) => !findNamedNoteValue(offered, option, spellings),
  );
  const isOptionChosen = (option: string) =>
    isChosen(option) || option === other;
  const takesOther =
    otherAllowed &&
    ownOptions.length < NOTE_OWN_VALUES_MAX_COUNT &&
    (!multiple || ownValues.length < NOTE_OWN_VALUES_MAX_COUNT);
  // A new option is the Other text; the values next to it were said.
  const valueBasis = block.basis === 'newOption' ? null : block.basis;
  // One value replaces the chosen one; several add to it. An own option of
  // a block that takes one is its own text.
  const choose = (value: string) =>
    onChange(
      multiple
        ? {
            values: [
              ...values.filter((item) => item === value || isChosen(item)),
              ...(values.includes(value) ? [] : [value]),
            ],
            other,
          }
        : offered.includes(value)
          ? { values: [value], other: '' }
          : { values: [], other: value },
    );
  // A block that takes several keeps its options open for the next value.
  const commitOther = (text: string, close?: () => void) => {
    setTypingOther(false);
    const known = findNamedNoteValue(
      [...offered, ...ownOptions],
      text,
      spellings,
    );
    if (!known) {
      onAddOption(text);
    } else if (!isOptionChosen(known)) {
      choose(known);
    }
    if (!multiple) {
      close?.();
    }
  };
  const otherLabel = formatMessage({
    id: 'global.other',
    defaultMessage: 'Other',
  });
  const renderOther = (close?: () => void) =>
    typingOther ? (
      <InlineValueInput
        className={styles.otherInput}
        value=""
        label={otherLabel}
        placeholder={otherLabel}
        autoFocus
        maxLength={NOTE_FREE_TEXT_MAX_LENGTH}
        onCommit={(text) => commitOther(text, close)}
        onCancel={() => setTypingOther(false)}
      />
    ) : (
      <button
        type="button"
        className={cn('p2', styles.textOption, styles.textAction)}
        disabled={!editable}
        data-option-row
        onClick={() => setTypingOther(true)}
      >
        {otherLabel}
      </button>
    );
  const tools = (
    <ValueTools
      block={block}
      listening={listening}
      onRevert={onRevert}
      onRelease={onRelease}
    />
  );

  if (!values.length && !otherAllowed) {
    return (
      <span className={cn('p2', styles.valueMark)}>
        <FormattedMessage
          id="workspace.noteEditor.noValues"
          defaultMessage="no options yet"
        />
      </span>
    );
  }

  if (asText) {
    // Before the recording, and while a change is unsent, the client prints
    // the clinician's selection the way scribe will; otherwise the text is
    // scribe's.
    const printed =
      mode === 'prefill' || block.pending
        ? none
          ? printNoteNone(choiceKind, language)
          : printNoteChoice({
              values: values
                .filter(isChosen)
                .map((value) => noteChoicePrint(def, value)),
              other,
            })
        : block.resolved
          ? block.text
          : '';
    // The own text of a block that takes one, when no own option names it,
    // is one more chosen option.
    const ownText =
      other && !ownOptions.includes(other) && !values.includes(other)
        ? [other]
        : [];
    const ownExtra = [
      ...ownOptions.filter((option) => !values.includes(option)),
      ...ownText,
    ];
    const shown = values.filter(
      (value, index) => index < NOTE_OPTIONS_VISIBLE || isChosen(value),
    );
    const folded = values.filter((value) => !shown.includes(value));
    const unchosen = values.filter((value) => !isChosen(value));
    const options = [...values, ...ownExtra];
    // The field's whole value goes: one value, or every value of a block
    // that takes several.
    const clearLabel = formatMessage({
      id: 'workspace.note.clearValue',
      defaultMessage: 'Clear the value',
    });
    // A value waiting for confirmation is rejected from its own control. A
    // value printed in the document is taken off from its own ✕ there.
    const clearButton = editable &&
      !approved &&
      !valueInDocument &&
      !listNoteBlockMarks(block, { editable }).confirm && (
        <button
          type="button"
          className={styles.clearValue}
          aria-label={clearLabel}
          title={clearLabel}
          data-component="workspace-note-clear-value"
          onClick={() => onChange({ values: [], other: '' })}
        >
          <Icon name="close" size={10} />
        </button>
      );
    const clearAfter = options.filter(isOptionChosen).at(-1);
    // A chosen option is taken off; another one is chosen by the block's
    // rule.
    const toggle = (value: string) => {
      if (!isOptionChosen(value)) {
        choose(value);
        return;
      }
      onChange(
        multiple
          ? {
              values: values.filter((item) => isChosen(item) && item !== value),
              other,
            }
          : { values: [], other: '' },
      );
    };
    const pick = (value: string, close?: () => void) => {
      if (!printed && !isOptionChosen(value)) {
        requestTriggerFocus(block.id);
      }
      toggle(value);
      if (!multiple) {
        close?.();
      }
    };
    const removeButton = (option: string) =>
      editable &&
      ownOptions.includes(option) &&
      !isOptionChosen(option) && (
        <button
          type="button"
          className={cn(styles.miniAction, styles.ownOptionRemove)}
          aria-label={formatMessage(
            {
              id: 'workspace.note.removeOwnOption',
              defaultMessage: 'Remove “{value}” from this note',
            },
            { value: option },
          )}
          onClick={() => onRemoveOption(option)}
        >
          <Icon name="close" size={12} />
        </button>
      );
    const renderOption = (value: string, close?: () => void) => {
      const chosen = isOptionChosen(value);
      // An own value AI chose is a new option; a template value was said.
      const basis = offered.includes(value) ? valueBasis : block.basis;
      const chip = (
        <Choice
          key={value}
          selected={chosen}
          ai={ai && chosen}
          optionValue={value}
          opens={optionLinks(block.id, value)}
          basis={basis}
          said={value === lastChosen ? block.said : undefined}
          rationale={block.rationale}
          aiHint={
            describeAiBasis(formatMessage, { ...block, basis }) ??
            formatMessage({
              id: 'workspace.note.aiValue',
              defaultMessage: 'Filled by AI. Click to change it.',
            })
          }
          asText={!images}
          className={cn(images && styles.imageChoice)}
          disabled={!editable}
          onClick={() => pick(value, close)}
        >
          {optionLabel(value)}
        </Choice>
      );
      const shownChip = ownOptions.includes(value) ? (
        <span
          key={value}
          className={styles.ownOption}
          data-component="workspace-note-own-option"
        >
          {chip}
          {removeButton(value)}
        </span>
      ) : (
        chip
      );
      // In the line, the last chosen chip ends with the clear.
      return value === clearAfter && !close ? (
        <Fragment key={value}>
          {shownChip}
          {clearButton}
        </Fragment>
      ) : (
        shownChip
      );
    };
    const chips = (
      <>
        {/* Before the start nothing is missing yet: no empty slot. */}
        {!printed && mode === 'note' && (
          <span
            className={cn('p2', styles.valueMark, styles.emptyLabel)}
            data-component="workspace-note-empty-value"
          >
            {emptyName}
          </span>
        )}
        {joinNoteOptions(
          [
            ...shown.map((value) => renderOption(value)),
            folded.length > 0 && editable && (
              <MoreOptions
                key="more"
                label={label}
                searchable
                trigger={
                  <button
                    type="button"
                    className={cn('p2', styles.textOption, styles.textAction)}
                    data-component="workspace-note-more-options"
                  >
                    <FormattedMessage
                      id="workspace.note.moreOptions"
                      defaultMessage="More ({count})…"
                      values={{ count: folded.length }}
                    />
                  </button>
                }
              >
                {({ query, close }) =>
                  unchosen
                    .filter((value) => matchesQuery(value, query))
                    .map((value) => renderOption(value, close))
                }
              </MoreOptions>
            ),
            ...ownExtra.map((value) => renderOption(value)),
            noneChip,
            takesOther && <Fragment key="other">{renderOther()}</Fragment>,
          ],
          images,
        )}
      </>
    );
    const printedClassName = cn(
      'p2',
      styles.choiceText,
      ai && styles.aiText,
      ai && block.basis === 'assumed' && styles.assumedText,
    );
    const printedTitle = ai ? describeAiBasis(formatMessage, block) : undefined;
    // Variant a: the chosen values print as text, and a click on them opens
    // every option of the block, the chosen ones checked.
    const optionsPopover = (
      <MoreOptions
        label={label}
        searchable={options.length > NOTE_OPTIONS_VISIBLE}
        listClassName={styles.checkOptions}
        focusKey={block.id}
        anchor={
          valueInDocument
            ? (trigger) => lastChosenValueNode(trigger, block.id)
            : undefined
        }
        trigger={
          <button
            type="button"
            className={
              valueInDocument
                ? styles.documentOptionsTrigger
                : cn(printedClassName, styles.printedTrigger)
            }
            aria-label={formatMessage(
              {
                id: 'workspace.note.blockValue',
                defaultMessage: '{label}: {value}',
              },
              { label, value: printed },
            )}
            title={printedTitle}
            data-component="workspace-note-choice-text"
          >
            {/* The value is text of the document; only the way to its
                options is left here, for the keyboard. */}
            {valueInDocument ? <Icon name="arrowDown" size={14} /> : printed}
          </button>
        }
      >
        {({ query, close }) => (
          <>
            {options
              .filter((value) => matchesQuery(value, query))
              .map((value) => {
                const chosen = isOptionChosen(value);
                return (
                  <CheckOption
                    key={value}
                    chosen={chosen}
                    after={removeButton(value)}
                    component={
                      ownOptions.includes(value)
                        ? 'workspace-note-own-option'
                        : undefined
                    }
                    onClick={() => {
                      if (multiple) {
                        toggle(value);
                        return;
                      }
                      if (!chosen) {
                        choose(value);
                      }
                      close();
                    }}
                  >
                    {optionLabel(value)}
                  </CheckOption>
                );
              })}
            {!!choiceKind.AllowNone && (
              <CheckOption
                chosen={none}
                toggleComponent="workspace-note-none"
                onClick={() => {
                  onNone(!none);
                  close();
                }}
              >
                {printNoteNone(choiceKind, language)}
              </CheckOption>
            )}
            {takesOther && (
              <span className={cn(styles.checkOption, styles.checkOther)}>
                {renderOther(close)}
              </span>
            )}
          </>
        )}
      </MoreOptions>
    );
    const printedText = (
      <span
        className={printedClassName}
        title={printedTitle}
        data-component="workspace-note-choice-text"
      >
        {printed}
      </span>
    );
    const printedOnly = approved || !!printed;

    return (
      <span
        className={styles.blockChoices}
        role="group"
        aria-label={label}
        data-component="workspace-note-choice"
      >
        {printedOnly
          ? printed && (
              <>
                {/* In the document the values are its text already. */}
                {valueInDocument
                  ? editable && !approved && optionsPopover
                  : (editable && !approved && optionsPopover) || printedText}
                {ai && (
                  <AiBasisMark
                    basis={block.basis}
                    said={block.said}
                    rationale={block.rationale}
                    fromInstruction={block.fromInstruction}
                  />
                )}
                {clearButton}
              </>
            )
          : chips}
        {tools}
      </span>
    );
  }

  const renderChoice = (value: string) => (
    <Choice
      key={value}
      opens={optionLinks(block.id, value)}
      selected={isChosen(value)}
      ai={ai && isChosen(value)}
      basis={valueBasis}
      said={value === lastChosen ? block.said : undefined}
      rationale={block.rationale}
      aiHint={describeAiBasis(formatMessage, { ...block, basis: valueBasis })}
      asText={!images}
      className={cn(images && styles.imageChoice)}
      disabled
    >
      {optionLabel(value)}
    </Choice>
  );
  const inLine = values.filter(
    (value, index) => index < NOTE_OPTIONS_VISIBLE || isChosen(value),
  );
  return (
    <span className={styles.blockChoices} role="group" aria-label={label}>
      {joinNoteOptions(
        [
          ...inLine.map((value) => renderChoice(value)),
          noneChip,
          takesOther && (
            <Choice
              key="other"
              className={cn(!other && styles.textAction)}
              selected={!!other}
              ai={ai && !!other}
              basis={block.basis}
              rationale={block.rationale}
              aiHint={describeAiBasis(formatMessage, block)}
              asText
              disabled
            >
              {other || otherLabel}
            </Choice>
          ),
        ],
        images,
      )}
      {tools}
    </span>
  );
};

type TeethBlockProps = {
  block: SurfaceBlock;
  def: PlainMessage<NoteTeethBlock>;
  multiple: boolean;
  notation: NoteToothNotation | undefined;
  mode: LineMode;
  editable: boolean;
  listening: boolean;
  approved: boolean;
  language?: string;
  nameInLine?: boolean;
  onChange: (teeth: string[], groups: NoteTeethGroup[]) => void;
  onRevert: () => void;
  onRelease: () => void;
};

type TeethMarksBlockProps = {
  block: SurfaceBlock;
  def: PlainMessage<NoteTeethBlock>;
  notation: NoteToothNotation | undefined;
  mode: LineMode;
  editable: boolean;
  approved: boolean;
  language?: string;
  nameInLine?: boolean;
  onMarks: (marks: NoteMark[]) => void;
  onNone: (none: boolean) => void;
};

// A teeth field that stores marks prints them as the note does; a click
// opens its chart, where each change is sent at once.
const TeethMarksBlock: FC<TeethMarksBlockProps> = ({
  block,
  def,
  notation,
  mode,
  editable,
  approved,
  language,
  nameInLine = false,
  onMarks,
  onNone,
}) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const { selection } = block;
  const marks = selection.marks ?? [];
  const none = !!selection.none;
  const label =
    def.Label ??
    formatMessage({
      id: 'workspace.note.selectTeeth',
      defaultMessage: 'Select teeth',
    });
  const emptyName =
    (!nameInLine && def.Label) ||
    formatMessage({
      id: 'workspace.note.teethPlaceholder',
      defaultMessage: 'teeth',
    });
  const ai = block.source === 'ai' && block.resolved;
  const waiting = block.unconfirmedMarks ?? [];
  const printed =
    mode === 'prefill' || block.pending
      ? none
        ? printNoteNone(def, language)
        : printNoteTeethMarks(def, marks, { notation, language })
      : block.resolved
        ? block.text
        : '';
  const printedClassName = cn(
    'p2',
    styles.choiceText,
    (ai || waiting.length > 0) && styles.aiText,
    ((ai && block.basis === 'assumed') || waiting.length > 0) &&
      styles.assumedText,
  );

  if (!editable || approved || mode === 'template') {
    return (
      <span
        className={printed ? printedClassName : cn('p2', styles.valueMark)}
        data-component="workspace-note-teeth-text"
      >
        {printed || emptyName}
      </span>
    );
  }
  return (
    <span className={styles.blockChoices} data-component="workspace-note-teeth">
      <BlockPopover
        label={label}
        open={open}
        contentClassName={cn(styles.popover, styles.widePopover)}
        trigger={
          <button
            type="button"
            className={
              printed
                ? cn(printedClassName, styles.printedTrigger)
                : cn(
                    'p2',
                    mode === 'note' ? styles.valueMark : styles.textOption,
                  )
            }
            aria-label={
              printed
                ? formatMessage(
                    {
                      id: 'workspace.note.blockValue',
                      defaultMessage: '{label}: {value}',
                    },
                    { label, value: printed },
                  )
                : label
            }
            data-component="workspace-note-teeth-text"
          >
            {printed || emptyName}
          </button>
        }
        onOpenChange={setOpen}
      >
        <NoteTeethMarks
          block={def}
          marks={marks}
          none={none}
          aiTeeth={block.aiMarkedTeeth ?? []}
          notation={notation}
          language={language}
          onChange={onMarks}
          onNone={onNone}
        />
        {waiting.length > 0 && (
          <ul
            className={styles.waitingParts}
            data-component="workspace-note-unconfirmed-marks"
          >
            {waiting.map(({ mark, quote }) => {
              const value = printNoteTeethMarks(def, [mark], {
                notation,
                language,
              });
              const same = (item: NoteMark) =>
                item.Tooth === mark.Tooth &&
                (item.Surface ?? '') === (mark.Surface ?? '');
              return (
                <li
                  key={`${mark.Tooth}/${mark.Surface ?? ''}`}
                  className="p3"
                  title={quote || undefined}
                >
                  <span className={cn(styles.aiText, styles.assumedText)}>
                    {value}
                  </span>
                  <AssumptionActions
                    confirmLabel={formatMessage({
                      id: 'workspace.note.confirmFieldParts',
                      defaultMessage: "Confirm all the field's suggestions",
                    })}
                    rejectLabel={formatMessage(
                      {
                        id: 'workspace.note.rejectAssumedPart',
                        defaultMessage: 'Remove “{value}”, which AI assumed',
                      },
                      { value },
                    )}
                    // The marks set as they are confirm what they keep.
                    onConfirm={() => onMarks(marks)}
                    onReject={() =>
                      onMarks(marks.filter((item) => !same(item)))
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </BlockPopover>
    </span>
  );
};

// The picker keeps its own selection and sends it once, when it closes. A
// template read before any note shows the value as a chip with a chevron;
// the fill surface prints it as text that opens the whole chart until the
// note is saved to the record.
const TeethBlock: FC<TeethBlockProps> = ({
  block,
  def,
  multiple,
  notation,
  mode,
  editable,
  listening,
  approved,
  language = '',
  nameInLine = false,
  onChange,
  onRevert,
  onRelease,
}) => {
  const { formatMessage } = useIntl();
  const teethGroupName = useNoteTeethGroupName(language);
  const emptyName =
    (!nameInLine && def.Label) ||
    formatMessage({
      id: 'workspace.note.teethPlaceholder',
      defaultMessage: 'teeth',
    });
  const [draft, setDraft] = useState<{
    teeth: string[];
    groups: NoteTeethGroup[];
  } | null>(null);
  const label =
    def.Label ??
    formatMessage({
      id: 'workspace.note.selectTeeth',
      defaultMessage: 'Select teeth',
    });
  const { selection } = block;
  const ai = block.source === 'ai' && block.resolved;
  const aiHint = ai ? describeAiBasis(formatMessage, block) : undefined;
  const aiTitle = ai
    ? aiHint ??
      formatMessage({
        id: 'workspace.note.aiValue',
        defaultMessage: 'Filled by AI. Click to change it.',
      })
    : undefined;
  const describeValue = (value: string) =>
    formatMessage(
      {
        id: 'workspace.note.blockValue',
        defaultMessage: '{label}: {value}',
      },
      { label, value },
    );
  const close = () => {
    if (!draft) {
      return;
    }
    const changed =
      draft.teeth.join() !== selection.teeth.join() ||
      draft.groups.join() !== selection.groups.join();
    setDraft(null);
    if (changed) {
      onChange(draft.teeth, draft.groups);
    }
  };
  const changeOpen = (open: boolean) =>
    open
      ? setDraft({ teeth: selection.teeth, groups: selection.groups })
      : close();
  const picker = (
    <>
      {draft && (
        <NoteTeethPicker
          block={def}
          teeth={draft.teeth}
          groups={draft.groups}
          notation={notation}
          multiple={multiple}
          restrictToBlock={false}
          onChange={(teeth, groups) => setDraft({ teeth, groups })}
        />
      )}
      <div className={styles.popoverFooter}>
        <ValueTools
          block={block}
          listening={listening}
          onRevert={onRevert}
          onRelease={onRelease}
        />
        <RadixPopover.Close asChild>
          <button type="button" className={cn('p3', styles.done)}>
            <FormattedMessage
              id="workspace.note.collapse"
              defaultMessage="Done"
            />
          </button>
        </RadixPopover.Close>
      </div>
    </>
  );

  if (mode === 'template') {
    return (
      <button
        type="button"
        className={cn(
          'p2',
          styles.textOption,
          block.resolved && styles.textChosen,
          ai && styles.aiText,
          ai && block.basis === 'assumed' && styles.assumedText,
        )}
        aria-label={block.resolved ? describeValue(block.text) : label}
        title={aiTitle}
        disabled
      >
        {block.resolved ? block.text : emptyName}
        {ai && (
          <AiBasisMark
            basis={block.basis}
            said={block.said}
            rationale={block.rationale}
            fromInstruction={block.fromInstruction}
          />
        )}
      </button>
    );
  }

  // Before the recording, and while a change is unsent, the client prints
  // the clinician's selection the way scribe will; otherwise the text is
  // scribe's.
  const printed =
    mode === 'prefill' || block.pending
      ? printNoteTeeth({
          teeth: selection.teeth,
          groups: selection.groups,
          notation,
          groupName: teethGroupName,
        })
      : block.resolved
        ? block.text
        : '';
  const printedClassName = cn(
    'p2',
    styles.choiceText,
    ai && styles.aiText,
    ai && block.basis === 'assumed' && styles.assumedText,
  );
  const aiMark = printed && ai && (
    <AiBasisMark
      basis={block.basis}
      said={block.said}
      rationale={block.rationale}
      fromInstruction={block.fromInstruction}
    />
  );

  if (approved) {
    if (!printed) {
      return null;
    }
    return (
      <span
        className={styles.blockChoices}
        data-component="workspace-note-teeth"
      >
        <span
          className={printedClassName}
          title={aiHint}
          data-component="workspace-note-teeth-text"
        >
          {printed}
        </span>
        {aiMark}
      </span>
    );
  }
  return (
    <span className={styles.blockChoices} data-component="workspace-note-teeth">
      <BlockPopover
        label={label}
        disabled={!editable}
        open={editable && !!draft}
        contentClassName={cn(styles.popover, styles.widePopover)}
        trigger={
          printed ? (
            <button
              type="button"
              className={cn(printedClassName, styles.printedTrigger)}
              aria-label={describeValue(printed)}
              title={aiTitle}
              data-component="workspace-note-teeth-text"
            >
              {printed}
            </button>
          ) : mode === 'note' ? (
            <button
              type="button"
              className={cn('p2', styles.valueMark)}
              aria-label={label}
              data-component="workspace-note-empty-value"
            >
              {emptyName}
            </button>
          ) : (
            <button
              type="button"
              className={cn('p2', styles.textOption)}
              aria-label={label}
            >
              {emptyName}
            </button>
          )
        }
        onOpenChange={changeOpen}
      >
        {picker}
      </BlockPopover>
      {aiMark}
    </span>
  );
};

type NumberBlockProps = {
  block: SurfaceBlock;
  def: PlainMessage<NoteNumberBlock>;
  mode: LineMode;
  editable: boolean;
  approved: boolean;
  language?: string;
  periodAfter: boolean;
  nameInLine?: boolean;
  // The field's values in its qualifier order; null clears it.
  onChange: (numbers: PlainMessage<NoteNumberValue>[] | null) => void;
};

// A number field: one box per qualifier ("справа [ ] мм, слева [ ] мм"), or
// one box, the unit after it. A comma is taken for the decimal point; Enter
// or leaving a box applies the field, held to its precision and range,
// Escape keeps what it held, and the ✕ clears it.
const NumberBlock: FC<NumberBlockProps> = ({
  block,
  def,
  mode,
  editable,
  approved,
  language,
  periodAfter,
  nameInLine = false,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const stored = block.selection.numbers ?? [];
  const qualifiers = def.Qualifiers.length
    ? def.Qualifiers
    : [NoteNumberQualifier.NoteNumberQualifierInvalidValue];
  const unit = endNoteValueBeforePeriod(
    printNoteUnit(def.Unit, language),
    periodAfter,
  );
  const label =
    def.Label ??
    formatMessage({
      id: 'workspace.note.enterNumber',
      defaultMessage: 'Enter a number',
    });
  // An empty number shows its name, else, where its line says it, what it
  // takes.
  const placeholder =
    (!nameInLine && def.Label) ||
    formatMessage({
      id: 'workspace.note.numberPlaceholder',
      defaultMessage: 'number',
    });
  const storedOf = (qualifier: number) =>
    stored.find((number) => number.Qualifier === qualifier)?.Value ?? '';
  const textOf = (qualifier: number) =>
    drafts[qualifier] ?? formatNoteDecimal(storedOf(qualifier), language);
  const parse = (qualifier: number) => {
    const text = textOf(qualifier).trim();
    if (!text) {
      return '';
    }
    const value = parseNoteDecimal(text, def.Precision ?? 0);
    return value !== null && isNoteNumberInRange(value, def) ? value : null;
  };

  if (mode === 'template') {
    return (
      <span className={cn('p2', styles.valueMark)}>
        <Icon name="ruler" size={14} />
        <span>{placeholder}</span>
      </span>
    );
  }
  if (!editable || approved) {
    const printed = endNoteValueBeforePeriod(
      block.text || printNoteNumbers(stored, def.Unit, language),
      periodAfter,
    );
    return (
      <span
        className={cn('p2', printed ? styles.choiceText : styles.valueMark)}
      >
        {printed || placeholder}
      </span>
    );
  }

  const apply = () => {
    const values = qualifiers.map((qualifier) => ({
      Qualifier: qualifier,
      Value: parse(qualifier),
    }));
    if (values.some(({ Value }) => Value === null)) {
      return;
    }
    setDrafts({});
    const numbers = values.filter(
      (number): number is PlainMessage<NoteNumberValue> => !!number.Value,
    );
    const unchanged =
      numbers.length === stored.length &&
      numbers.every((number) => storedOf(number.Qualifier) === number.Value);
    if (!unchanged) {
      onChange(numbers.length ? numbers : null);
    }
  };

  return (
    <span
      className={styles.blockChoices}
      data-component="workspace-note-number"
    >
      {qualifiers.map((qualifier, index) => {
        const word = printNoteQualifier(qualifier, language);
        const invalid = parse(qualifier) === null;
        return (
          <Fragment key={qualifier}>
            {index > 0 && ', '}
            <span className={styles.numberPart}>
              {word && <span className="p2">{word}</span>}
              <input
                className={cn(
                  'p2',
                  styles.numberValue,
                  invalid && styles.invalidNumber,
                )}
                inputMode="decimal"
                aria-label={word ? `${label}: ${word}` : label}
                aria-invalid={invalid}
                placeholder={placeholder}
                style={{
                  ['--number-chars' as string]:
                    textOf(qualifier).length ||
                    Math.max(placeholder.length, NOTE_NUMBER_MIN_CHARS),
                }}
                value={textOf(qualifier)}
                onChange={(event) =>
                  setDrafts((current) => ({
                    ...current,
                    [qualifier]: event.target.value,
                  }))
                }
                onBlur={apply}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    apply();
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setDrafts({});
                  }
                }}
              />
              {unit && <span className="p2">{unit}</span>}
            </span>
          </Fragment>
        );
      })}
      {stored.length > 0 && (
        <button
          type="button"
          className={styles.clearValue}
          aria-label={formatMessage({
            id: 'workspace.note.clearValue',
            defaultMessage: 'Clear the value',
          })}
          data-component="workspace-note-clear-value"
          onClick={() => {
            setDrafts({});
            onChange(null);
          }}
        >
          <Icon name="close" size={10} />
        </button>
      )}
    </span>
  );
};

type DateBlockProps = {
  block: SurfaceBlock;
  def: PlainMessage<NoteDateBlock>;
  mode: LineMode;
  editable: boolean;
  approved: boolean;
  language?: string;
  nameInLine?: boolean;
  // A YYYY-MM-DD date; empty clears the field.
  onChange: (date: string) => void;
};

// A date field. At rest it prints the date as the note does, in the
// template's language; a click opens the calendar, where a date is chosen
// or typed in the interface language's format. Enter or leaving applies it,
// Escape keeps the date as it was, and the ✕ clears it.
const DateBlock: FC<DateBlockProps> = ({
  block,
  def,
  mode,
  editable,
  approved,
  language,
  nameInLine = false,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const [editing, setEditing] = useState(false);
  const label =
    def.Label ??
    formatMessage({
      id: 'workspace.note.selectDate',
      defaultMessage: 'Select a date',
    });
  const emptyName =
    (!nameInLine && def.Label) ||
    formatMessage({
      id: 'workspace.note.datePlaceholder',
      defaultMessage: 'date',
    });
  const stored = block.selection.date;
  const printed =
    mode === 'prefill' || block.pending
      ? printNoteDate(stored, language)
      : block.resolved
        ? block.text
        : '';
  const clearLabel = formatMessage({
    id: 'workspace.note.clearValue',
    defaultMessage: 'Clear the value',
  });

  if (mode === 'template') {
    return (
      <span className={cn('p2', styles.valueMark)}>
        <Icon name="calendar" size={14} />
        <span>{emptyName}</span>
      </span>
    );
  }
  if (!editable || approved) {
    return (
      <span
        className={cn('p2', printed ? styles.choiceText : styles.valueMark)}
      >
        {printed || emptyName}
      </span>
    );
  }

  if (editing) {
    return (
      <span className={styles.dateInPlace}>
        <span className={cn('p2', styles.dateHeld)} aria-hidden="true">
          {printed || emptyName}
        </span>
        <NoteDatePicker
          value={stored}
          onClose={(next) => {
            setEditing(false);
            if (next !== undefined) {
              onChange(next);
            }
          }}
        />
      </span>
    );
  }

  return (
    <span className={styles.blockChoices} data-component="workspace-note-date">
      <button
        type="button"
        className={cn(
          'p2',
          printed
            ? [styles.choiceText, styles.printedTrigger]
            : mode === 'note'
              ? styles.valueMark
              : styles.textOption,
        )}
        aria-label={
          printed
            ? formatMessage(
                {
                  id: 'workspace.note.blockValue',
                  defaultMessage: '{label}: {value}',
                },
                { label, value: printed },
              )
            : label
        }
        onClick={() => setEditing(true)}
      >
        {printed || emptyName}
      </button>
      {printed && !listNoteBlockMarks(block, { editable }).confirm && (
        <button
          type="button"
          className={styles.clearValue}
          aria-label={clearLabel}
          title={clearLabel}
          data-component="workspace-note-clear-value"
          onClick={() => onChange('')}
        >
          <Icon name="close" size={10} />
        </button>
      )}
    </span>
  );
};

// Fixed wording as the note prints it; the words an after-Stop AI edit wrote
// are highlighted.
const NoteWording: FC<{ block: SurfaceBlock }> = ({ block }) => {
  const aiEdited = block.spans.some((span) => span.ai);
  return (
    <span
      className={cn(
        'p2',
        styles.wording,
        block.source === 'ai' && !aiEdited && styles.aiText,
        block.source === 'manual' && styles.editedText,
      )}
    >
      {block.spans.length
        ? block.spans.map((span, index) => (
            <span
              key={index}
              className={cn(span.ai && styles.aiSpan)}
              data-component={span.ai ? 'workspace-note-ai-edit' : undefined}
            >
              {stripInlineGuidance(span.text)}
            </span>
          ))
        : stripInlineGuidance(block.text)}
    </span>
  );
};

// A kind this version of the client does not know, or a shape of a known
// kind it cannot edit yet (a teeth field that stores marks): what the note
// prints for it, else its label, read-only.
const UnknownBlockValue: FC<{ block: SurfaceBlock }> = ({ block }) => {
  const label = (block.def?.Kind.value as { Label?: string } | undefined)
    ?.Label;
  return (
    <span
      className={cn('p2', styles.valueMark)}
      data-component="workspace-note-unknown-block"
    >
      {(block.resolved && block.text) || label || (
        <FormattedMessage
          id="workspace.templateEdit.unknownBlock"
          defaultMessage="A block this version cannot show"
        />
      )}
    </span>
  );
};

type BlockValueProps = {
  block: SurfaceBlock;
  lineId: string;
  mode: LineMode;
  lists: NoteList[];
  notation: NoteToothNotation | undefined;
  editable: boolean;
  listening: boolean;
  approved: boolean;
  language?: string;
  // A choice's chosen values are text of the note's document.
  valueInDocument?: boolean;
  // The line's text right after the block starts with a period.
  periodAfter?: boolean;
  // The line's wording says the field's name: the empty field shows what it
  // takes in a word instead.
  nameInLine?: boolean;
  onOperation: (operation: NoteOperation, undoable?: boolean) => void;
};

// One block in its line: the printed text comes from scribe's view (or the
// template before the recording), the chosen values from its answer.
const BlockValue: FC<BlockValueProps> = ({
  block,
  lineId,
  mode,
  lists,
  notation,
  editable,
  listening,
  approved,
  language,
  valueInDocument,
  periodAfter = false,
  nameInLine,
  onOperation,
}) => {
  const { formatMessage } = useIntl();
  const { def } = block;
  const revert = () => onOperation(revertNoteBlock(block.id, lineId));
  const release = () => onOperation(releaseNoteBlock(block.id, lineId));

  if (!def) {
    return <span className={cn('p2', styles.wording)}>{block.text}</span>;
  }
  switch (def.Kind.case) {
    case 'Text':
      if (mode !== 'template') {
        return <NoteWording block={block} />;
      }
      return (
        <span className={cn('p2', styles.wording)}>
          <Wording value={def.Kind.value.Value} />
        </span>
      );
    case 'Choice':
    case 'MultiChoice':
    case 'ListRef':
      return (
        <ChoiceBlock
          block={block}
          def={def}
          lists={lists}
          mode={mode}
          editable={editable}
          listening={listening}
          approved={approved}
          valueInDocument={valueInDocument}
          language={language}
          nameInLine={nameInLine}
          onNone={(none) =>
            onOperation(
              none
                ? setNoteBlock(block.id, lineId, { none })
                : clearNoteBlock(block.id, lineId),
            )
          }
          onChange={({ values, other }) =>
            onOperation(
              values.length || other
                ? setNoteBlock(block.id, lineId, { values, text: other })
                : clearNoteBlock(block.id, lineId),
            )
          }
          onAddOption={(text) =>
            onOperation(addNoteOwnOption(block.id, lineId, text))
          }
          onRemoveOption={(option) =>
            onOperation(removeNoteOwnOption(block.id, lineId, option))
          }
          onRevert={revert}
          onRelease={release}
        />
      );
    case 'Teeth':
      if (noteTeethHaveMarks(def.Kind.value)) {
        return (
          <TeethMarksBlock
            block={block}
            def={def.Kind.value}
            notation={notation}
            mode={mode}
            editable={editable}
            approved={approved}
            nameInLine={nameInLine}
            language={language}
            onMarks={(marks) =>
              onOperation(
                marks.length
                  ? setNoteBlock(block.id, lineId, { marks })
                  : clearNoteBlock(block.id, lineId),
              )
            }
            onNone={(none) =>
              onOperation(
                none
                  ? setNoteBlock(block.id, lineId, { none })
                  : clearNoteBlock(block.id, lineId),
              )
            }
          />
        );
      }
      return (
        <TeethBlock
          block={block}
          def={def.Kind.value}
          multiple={isNoteBlockMultiple(def)}
          notation={notation}
          mode={mode}
          editable={editable}
          listening={listening}
          approved={approved}
          language={language}
          nameInLine={nameInLine}
          onChange={(teeth, teethGroups) =>
            onOperation(
              teeth.length || teethGroups.length
                ? setNoteBlock(block.id, lineId, { teeth, teethGroups })
                : clearNoteBlock(block.id, lineId),
            )
          }
          onRevert={revert}
          onRelease={release}
        />
      );
    case 'FreeText': {
      const placeholder =
        (!nameInLine && def.Kind.value.Label) ||
        formatMessage({
          id: 'workspace.note.freeTextPlaceholder',
          defaultMessage: 'text',
        });
      const ai = block.source === 'ai' && block.resolved;
      return (
        <span
          className={cn(
            'p2',
            styles.valueMark,
            block.resolved && styles.slotFilled,
            ai && styles.slotAi,
            ai && block.basis === 'assumed' && styles.slotAssumed,
          )}
          title={ai ? describeAiBasis(formatMessage, block) : undefined}
        >
          {block.resolved ? block.text : placeholder}
        </span>
      );
    }
    case 'Context': {
      const name = formatMessage(getNoteContextKeyMessage(def.Kind.value.Key));
      if (mode !== 'note') {
        return (
          <span
            className={cn('p2', styles.valueMark)}
            title={formatMessage({
              id: 'workspace.note.contextHint',
              defaultMessage: 'Taken from the encounter and the patient record',
            })}
          >
            <Icon name="user" size={14} />
            <span>{name}</span>
          </span>
        );
      }
      if (block.resolved || !editable) {
        return (
          <span
            className={cn(
              'p2',
              styles.valueMark,
              block.resolved && styles.slotFilled,
            )}
            title={formatMessage({
              id: 'workspace.note.contextHint',
              defaultMessage: 'Taken from the encounter and the patient record',
            })}
          >
            {block.resolved ? (
              block.text
            ) : (
              <FormattedMessage
                id="workspace.note.contextMissing"
                defaultMessage="no data"
              />
            )}
          </span>
        );
      }
      return (
        <InlineValueInput
          value=""
          label={formatMessage({
            id: 'workspace.note.contextMissingInput',
            defaultMessage: 'No data in the record: type the value',
          })}
          placeholder={formatMessage({
            id: 'workspace.note.contextMissing',
            defaultMessage: 'no data',
          })}
          maxLength={NOTE_OVERRIDE_MAX_LENGTH}
          onCommit={(text) =>
            onOperation(setNoteBlock(block.id, lineId, { text }))
          }
        />
      );
    }
    case 'Number':
      return (
        <NumberBlock
          block={block}
          def={def.Kind.value}
          mode={mode}
          editable={editable}
          approved={approved}
          language={language}
          periodAfter={periodAfter}
          nameInLine={nameInLine}
          onChange={(numbers) =>
            onOperation(
              numbers
                ? setNoteBlock(block.id, lineId, { numbers })
                : clearNoteBlock(block.id, lineId),
            )
          }
        />
      );
    case 'Table':
      return (
        <NoteTableField
          block={block}
          def={def.Kind.value}
          mode={mode}
          editable={editable}
          approved={approved}
          notation={notation}
          language={language}
          onChange={(tableRows) =>
            onOperation(
              tableRows
                ? setNoteBlock(block.id, lineId, { tableRows })
                : clearNoteBlock(block.id, lineId),
            )
          }
        />
      );
    case 'Date':
      return (
        <DateBlock
          block={block}
          def={def.Kind.value}
          mode={mode}
          editable={editable}
          approved={approved}
          language={language}
          nameInLine={nameInLine}
          onChange={(date) =>
            onOperation(
              date
                ? setNoteBlock(block.id, lineId, { date })
                : clearNoteBlock(block.id, lineId),
            )
          }
        />
      );
    default:
      return <UnknownBlockValue block={block} />;
  }
};

type NoteBlockViewProps = {
  block: SurfaceBlock;
  lineId: string;
  mode: LineMode;
  lists: NoteList[];
  notation?: NoteToothNotation;
  editable: boolean;
  listening: boolean;
  approved?: boolean;
  language?: string;
  // A choice's chosen values are text of the note's document.
  valueInDocument?: boolean;
  // The line's text right after the block starts with a period.
  periodAfter?: boolean;
  // The line's wording says the field's name: the empty field shows what it
  // takes in a word instead.
  nameInLine?: boolean;
  onOperation: (operation: NoteOperation, undoable?: boolean) => void;
};

type BlockMarksProps = {
  block: SurfaceBlock;
  lineId: string;
  editable: boolean;
  onOperation: (operation: NoteOperation, undoable?: boolean) => void;
};

// What AI did to a block and what it asks of the clinician, after its value.
const NoteBlockMarks: FC<BlockMarksProps> = ({
  block,
  lineId,
  editable,
  onOperation,
}) => {
  const { formatMessage } = useIntl();
  const marks = listNoteBlockMarks(block, { editable });
  const suggested = block.basis === 'suggested';
  const confirmLabel = suggested
    ? formatMessage({
        id: 'workspace.note.confirmSuggestion',
        defaultMessage: 'Confirm the value AI suggested',
      })
    : formatMessage({
        id: 'workspace.note.confirmAssumption',
        defaultMessage: 'Confirm the value AI assumed',
      });
  const rejectLabel = suggested
    ? formatMessage({
        id: 'workspace.note.rejectSuggestion',
        defaultMessage: 'Reject the value AI suggested and clear it',
      })
    : formatMessage({
        id: 'workspace.note.rejectAssumption',
        defaultMessage: 'Reject the value AI assumed and clear it',
      });
  return (
    <>
      {marks.addedByAi && (
        <>
          <AiMark />
          {editable && (
            <button
              type="button"
              className={styles.miniAction}
              aria-label={formatMessage({
                id: 'workspace.note.removeAiText',
                defaultMessage: 'Remove the text AI added',
              })}
              onClick={() =>
                onOperation(
                  changeNoteAddedBlock(
                    block.id,
                    lineId,
                    NoteAddedBlockOp.NoteAddedBlockOpRemove,
                  ),
                )
              }
            >
              <Icon name="close" size={14} />
            </button>
          )}
        </>
      )}
      {marks.confirm && (
        <AssumptionActions
          confirmLabel={confirmLabel}
          rejectLabel={rejectLabel}
          onConfirm={() =>
            onOperation(confirmNoteBlock(block.id, lineId, block.selection))
          }
          onReject={() => onOperation(clearNoteBlock(block.id, lineId))}
        />
      )}
      {marks.quote && <AiBasisQuote quote={block.quote} said={block.said} />}
      {block.correction !== undefined && (
        <CorrectionHint
          text={block.correction}
          editable={editable}
          onApply={() => onOperation(applyNoteCorrection(block.id, lineId))}
          onDismiss={() =>
            onOperation(dismissNoteCorrection(block.id, lineId), false)
          }
        />
      )}
    </>
  );
};

// One block of a line on the note: its value, and the marks and actions of
// what AI did to it.
export const NoteBlockView: FC<NoteBlockViewProps> = ({
  block,
  lineId,
  mode,
  lists,
  notation,
  editable,
  listening,
  approved = false,
  language,
  valueInDocument,
  periodAfter,
  nameInLine,
  onOperation,
}) => (
  <span
    className={cn(
      styles.block,
      block.pending && styles.pendingBlock,
      block.addedByAi && styles.addedByAi,
    )}
    data-component="workspace-template-block"
    data-block-id={block.id}
  >
    {listNoteBlockMarks(block, { editable }).partnerDefault && (
      <PartnerDefaultHint text={block.partnerDefault} />
    )}
    <BlockValue
      block={block}
      lineId={lineId}
      mode={mode}
      lists={lists}
      notation={notation}
      editable={editable}
      listening={listening}
      approved={approved}
      language={language}
      valueInDocument={valueInDocument}
      periodAfter={periodAfter}
      nameInLine={nameInLine}
      onOperation={onOperation}
    />
    <NoteBlockMarks
      block={block}
      lineId={lineId}
      editable={editable}
      onOperation={onOperation}
    />
  </span>
);

type TextBlockAsideProps = BlockMarksProps & { listening: boolean };

// What a text block of the document shows right after its text: the way
// back to the template wording, a free text's AI basis and the way back to
// the AI value, and the marks every block shows.
export const NoteTextBlockAside: FC<TextBlockAsideProps> = ({
  block,
  lineId,
  editable,
  listening,
  onOperation,
}) => {
  const { formatMessage } = useIntl();
  const aside = listTextBlockAside(block, { editable, listening });
  const restoreLabel = formatMessage({
    id: 'workspace.note.restoreTemplateText',
    defaultMessage: 'Back to the template wording',
  });
  return (
    <span
      className={cn(styles.block, styles.textAside)}
      data-component="workspace-note-block-aside"
    >
      {aside.partnerDefault && (
        <PartnerDefaultHint text={block.partnerDefault} />
      )}
      {aside.restoreWording && (
        <button
          type="button"
          className={styles.miniAction}
          aria-label={restoreLabel}
          title={restoreLabel}
          onClick={() => onOperation(clearNoteBlock(block.id, lineId))}
        >
          <Icon name="editorUndo" size={14} />
        </button>
      )}
      {aside.aiBasis && (
        <AiBasisMark
          basis={block.basis}
          said={block.said}
          rationale={block.rationale}
          fromInstruction={block.fromInstruction}
        />
      )}
      {aside.valueTools && (
        <ValueTools
          block={block}
          listening={listening}
          onRevert={() => onOperation(revertNoteBlock(block.id, lineId))}
          onRelease={() => onOperation(releaseNoteBlock(block.id, lineId))}
        />
      )}
      <NoteBlockMarks
        block={block}
        lineId={lineId}
        editable={editable}
        onOperation={onOperation}
      />
    </span>
  );
};

type LineViewProps = {
  line: SurfaceLine;
  mode: LineMode;
  lists: NoteList[];
  notation?: NoteToothNotation;
  // The template's language, which the note is printed in.
  language?: string;
  className?: string;
};

// One line of a block template read as a sentence: wording, values and
// record data in place, as a field row shows its chips. The note's lines
// are edited in its document instead.
export const TemplateLineView: FC<LineViewProps> = ({
  line,
  mode,
  lists,
  notation,
  language,
  className,
}) => (
  <div className={cn('p2', styles.line, className)}>
    {line.addedByAi && <AiMark />}
    {line.blocks.map((block, index) => (
      <Fragment key={block.id}>
        {index > 0 && !startsWithPunctuation(block) && ' '}
        <NoteBlockView
          block={block}
          lineId={line.id}
          periodAfter={isNotePeriodAfter(line, index)}
          nameInLine={isNoteFieldNamedByLine(line, block)}
          mode={mode}
          lists={lists}
          notation={notation}
          editable={false}
          listening={false}
          language={language}
          onOperation={noop}
        />
      </Fragment>
    ))}
    {line.note && (
      <span className={cn('p2', styles.lineNoteText)}>{line.note}</span>
    )}
    {line.teethMissing && !line.blocks.some((block) => block.pending) && (
      <PartnerTeethMissing />
    )}
  </div>
);
